import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Save } from 'lucide-react';
import axios from '../axiosConfig';
import { getChangedFields } from '../utils/changeTracker';
import { getLaunchPageOptions } from '../utils/launchPagePaths';
import { usePreferences } from '../contexts/PreferenceContext';
import DynamicPreferenceField from './preferences/DynamicPreferenceField';
import { getCategoryDisplayName, getCategoryIcon } from '../utils/preferenceIcons';
import { useTheme } from '../contexts/ThemeContext';
import { ThemedButton, ThemedCard } from './themed';
import ConfirmDialogShell from './dialogs/ConfirmDialogShell';

const valuesEqual = (left, right) => JSON.stringify(left) === JSON.stringify(right);

const PreferenceSettings = ({ entities = [], currentEntity, onEntityPreferenceSuccess }) => {
  const { t } = useTranslation();
  const { text, cn } = useTheme();
  const preferenceContext = usePreferences();
  const entityId = Number(currentEntity?.entityId);
  const canEdit = (
    currentEntity?.canEditUserEntityPreferences === true &&
    Number.isInteger(entityId) &&
    entityId > 0
  );

  const [loading, setLoading] = useState(canEdit);
  const [saving, setSaving] = useState(false);
  const [primarySaving, setPrimarySaving] = useState(false);
  const [activeTab, setActiveTab] = useState('display');
  const [preferenceDefinitions, setPreferenceDefinitions] = useState({});
  const [definitionError, setDefinitionError] = useState(false);
  const [globalDraft, setGlobalDraft] = useState({});
  const [globalBaseline, setGlobalBaseline] = useState({});
  const [launchDraft, setLaunchDraft] = useState('');
  const [launchBaseline, setLaunchBaseline] = useState('');
  const [confirmPrimaryOpen, setConfirmPrimaryOpen] = useState(false);

  const globalDraftRef = useRef(globalDraft);
  const globalBaselineRef = useRef(globalBaseline);
  const contextUserPreferencesRef = useRef(preferenceContext.userPreferences || {});
  const savingRef = useRef(false);
  const selectionVersionRef = useRef(0);
  contextUserPreferencesRef.current = preferenceContext.userPreferences || {};

  const setGlobalDraftValue = (next) => {
    globalDraftRef.current = next;
    setGlobalDraft(next);
  };

  const setGlobalBaselineValue = (next) => {
    globalBaselineRef.current = next;
    setGlobalBaseline(next);
  };

  const allDefinitions = useMemo(
    () => Object.values(preferenceDefinitions).flat(),
    [preferenceDefinitions]
  );
  const launchDefinition = useMemo(
    () => allDefinitions.find(definition => (
      definition.preference_key === 'default_launch_page' &&
      definition.preference_type === 'user_entity'
    )),
    [allDefinitions]
  );
  const globalDefinitionsByCategory = useMemo(() => {
    return Object.entries(preferenceDefinitions).reduce((categories, [category, definitions]) => {
      if (category === 'notification') return categories;
      const globalDefinitions = definitions.filter(definition => definition.preference_type === 'user_global');
      if (globalDefinitions.length > 0) categories[category] = globalDefinitions;
      return categories;
    }, {});
  }, [preferenceDefinitions]);
  const availableCategories = useMemo(
    () => Object.keys(globalDefinitionsByCategory),
    [globalDefinitionsByCategory]
  );
  const launchOptions = useMemo(
    () => getLaunchPageOptions(launchDefinition?.allowed_values, currentEntity?.membershipRole),
    [launchDefinition, currentEntity?.membershipRole]
  );
  const actualMemberships = useMemo(
    () => entities.filter(entity => entity.hasActiveMembership === true),
    [entities]
  );
  const previousPrimary = useMemo(
    () => actualMemberships.find(entity => entity.is_primary && String(entity.entityId) !== String(entityId)),
    [actualMemberships, entityId]
  );

  useEffect(() => {
    selectionVersionRef.current += 1;
    const incomingGlobal = contextUserPreferencesRef.current;
    setGlobalDraftValue({ ...incomingGlobal });
    setGlobalBaselineValue({ ...incomingGlobal });
    setPreferenceDefinitions({});
    setDefinitionError(false);
    setLaunchDraft('');
    setLaunchBaseline('');
    setConfirmPrimaryOpen(false);

    if (!canEdit) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    axios.get(`/api/settings/${entityId}/preferences/definitions`)
      .then(response => {
        if (cancelled) return;
        if (response.data?.success !== true || !response.data?.data) {
          throw new Error('Invalid preference definition response');
        }
        setPreferenceDefinitions(response.data.data);
      })
      .catch(() => {
        if (!cancelled) {
          setDefinitionError(true);
          toast.error(t('errorFetchingPreferences'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canEdit, entityId, t]);

  // External global updates (notably language) may hydrate clean fields, but
  // must not erase a field the user is currently editing or any save draft.
  useEffect(() => {
    if (savingRef.current) return;
    const incoming = preferenceContext.userPreferences || {};
    const previousBaseline = globalBaselineRef.current;
    const previousDraft = globalDraftRef.current;
    const nextDraft = { ...previousDraft };

    Object.entries(incoming).forEach(([key, value]) => {
      if (valuesEqual(previousDraft[key], previousBaseline[key])) {
        nextDraft[key] = value;
      }
    });

    setGlobalBaselineValue({ ...previousBaseline, ...incoming });
    setGlobalDraftValue(nextDraft);
  }, [preferenceContext.userPreferences]);

  useEffect(() => {
    if (!canEdit || launchOptions.length === 0) return;
    const storedValue = preferenceContext.entityPreferences?.default_launch_page
      ?? currentEntity?.default_launch_page;
    const selectedValue = launchOptions.some(option => option.value === storedValue)
      ? storedValue
      : launchOptions[0].value;

    setLaunchDraft(selectedValue);
    setLaunchBaseline(typeof storedValue === 'string' && storedValue ? storedValue : selectedValue);
  }, [canEdit, currentEntity?.default_launch_page, launchOptions, preferenceContext.entityPreferences?.default_launch_page]);

  useEffect(() => {
    const nextTab = availableCategories[0] || (canEdit ? 'entities' : 'display');
    if (!availableCategories.includes(activeTab) && activeTab !== 'entities') {
      setActiveTab(nextTab);
    }
  }, [activeTab, availableCategories, canEdit]);

  const handleGlobalChange = (key, value) => {
    setGlobalDraftValue({ ...globalDraftRef.current, [key]: value });
  };

  const handleSavePreferences = async () => {
    if (!canEdit || savingRef.current) return;

    const capturedEntityId = entityId;
    const capturedSelectionVersion = selectionVersionRef.current;
    const capturedGlobalDraft = { ...globalDraftRef.current };
    const capturedGlobalBaseline = { ...globalBaselineRef.current };
    const globalDefinitionKeys = new Set(
      Object.values(globalDefinitionsByCategory).flat().map(definition => definition.preference_key)
    );
    const changedGlobal = Object.fromEntries(
      Object.entries(getChangedFields(capturedGlobalBaseline, capturedGlobalDraft))
        .filter(([key]) => globalDefinitionKeys.has(key))
    );
    const launchChanged = (
      launchOptions.some(option => option.value === launchDraft) &&
      !valuesEqual(launchDraft, launchBaseline)
    );

    if (Object.keys(changedGlobal).length === 0 && !launchChanged) return;

    savingRef.current = true;
    setSaving(true);
    let failed = false;
    const receivedGlobal = {};

    try {
      for (const [key, value] of Object.entries(changedGlobal)) {
        try {
          const acknowledged = await preferenceContext.updateGlobalPreference(
            key,
            value,
            capturedEntityId
          );
          if (!acknowledged) throw new Error('Preference write was not acknowledged');
          receivedGlobal[key] = value;
        } catch (_) {
          failed = true;
        }
      }

      let launchSaved = false;
      if (launchChanged) {
        try {
          const response = await axios.put(`/api/settings/entity/${capturedEntityId}/preference`, {
            preference: 'default_launch_page',
            value: launchDraft
          });
          if (response.data?.success !== true) throw new Error('Launch preference write was not acknowledged');
          launchSaved = true;
          await preferenceContext.publishReceivedPreferenceSuccess(capturedEntityId, {
            userEntity: { default_launch_page: launchDraft }
          });
          onEntityPreferenceSuccess?.(capturedEntityId, { default_launch_page: launchDraft });
        } catch (_) {
          failed = true;
        }
      }

      if (selectionVersionRef.current === capturedSelectionVersion) {
        if (Object.keys(receivedGlobal).length > 0) {
          setGlobalBaselineValue({ ...globalBaselineRef.current, ...receivedGlobal });
        }
        if (launchSaved) setLaunchBaseline(launchDraft);
      }

      if (failed) {
        toast.error(t('errorUpdatingPreferences'));
      } else {
        toast.success(t('preferencesUpdatedSuccessfully'));
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const confirmPrimaryEntity = async () => {
    if (!canEdit || primarySaving) return;
    const capturedEntityId = entityId;
    setPrimarySaving(true);
    try {
      const response = await axios.put(`/api/settings/primary-entity/${capturedEntityId}`);
      if (response.data?.success !== true) throw new Error('Primary transition was not acknowledged');
      await preferenceContext.publishReceivedPreferenceSuccess(capturedEntityId, {
        userEntity: { is_primary: true }
      });
      onEntityPreferenceSuccess?.(capturedEntityId, {}, { primaryTransition: true });
      setConfirmPrimaryOpen(false);
      toast.success(t('preferencesUpdatedSuccessfully'));
    } catch (error) {
      toast.error(error.response?.data?.error || t('errorUpdatingPreferences'));
    } finally {
      setPrimarySaving(false);
    }
  };

  if (!canEdit) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className={cn('text-2xl font-bold', text.primary)}>{t('preferences')}</h2>
          <p className={cn('mt-1 text-sm', text.muted)}>{t('customizeYourExperience')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  const launchUnavailable = definitionError || !launchDefinition || launchOptions.length === 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className={cn('text-2xl font-bold', text.primary)}>{t('preferences')}</h2>
        <p className={cn('mt-1 text-sm', text.muted)}>{t('customizeYourExperience')}</p>
      </div>

      <div className={cn('border-b', 'border-gray-200 dark:border-gray-700')}>
        <nav className="-mb-px flex space-x-8">
          {availableCategories.map(category => {
            const CategoryIcon = getCategoryIcon(category);
            return (
              <button
                key={category}
                onClick={() => setActiveTab(category)}
                className={cn(
                  'py-2 px-1 border-b-2 font-medium text-sm flex items-center transition-colors',
                  activeTab === category
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : cn('border-transparent', text.muted, 'hover:border-gray-300 dark:hover:border-gray-600')
                )}
              >
                <CategoryIcon className="w-4 h-4 mr-1" />
                {t(getCategoryDisplayName(category))}
              </button>
            );
          })}
          <button
            onClick={() => setActiveTab('entities')}
            className={cn(
              'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
              activeTab === 'entities'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : cn('border-transparent', text.muted, 'hover:border-gray-300 dark:hover:border-gray-600')
            )}
          >
            {t('entitySettings')}
          </button>
        </nav>
      </div>

      {availableCategories.map(category => activeTab === category && (
        <ThemedCard key={category}>
          <ThemedCard.Body className="space-y-6">
            {globalDefinitionsByCategory[category].map(definition => (
              <DynamicPreferenceField
                key={definition.preference_key}
                definition={definition}
                value={globalDraft[definition.preference_key]}
                onChange={handleGlobalChange}
                disabled={saving}
              />
            ))}
          </ThemedCard.Body>
        </ThemedCard>
      ))}

      {activeTab === 'entities' && (
        <ThemedCard>
          <ThemedCard.Header>{currentEntity.entityName}</ThemedCard.Header>
          <ThemedCard.Body className="space-y-5">
            {actualMemberships.length > 1 && (
              <label className="flex items-center">
                <input
                  type="radio"
                  name="primary_entity"
                  checked={currentEntity.is_primary === true}
                  onChange={() => {
                    if (!currentEntity.is_primary) setConfirmPrimaryOpen(true);
                  }}
                  disabled={saving || primarySaving}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className={cn('ml-2 text-sm', text.secondary)}>{t('setPrimaryEntity')}</span>
              </label>
            )}

            <div>
              <label htmlFor="default-launch-page" className={cn('block text-sm font-medium mb-1', text.secondary)}>
                {t('defaultLaunchPage')}
              </label>
              {launchUnavailable ? (
                <p className={cn('text-sm', text.muted)}>{t('errorFetchingPreferences')}</p>
              ) : (
                <select
                  id="default-launch-page"
                  value={launchDraft}
                  onChange={event => setLaunchDraft(event.target.value)}
                  disabled={saving}
                  className={cn(
                    'w-full px-3 py-2 border rounded-md transition-all duration-200',
                    'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700',
                    text.primary,
                    'focus:ring-blue-500 focus:border-blue-500 focus:ring-2'
                  )}
                >
                  {launchOptions.map(option => (
                    <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                  ))}
                </select>
              )}
            </div>
          </ThemedCard.Body>
        </ThemedCard>
      )}

      <div className="flex justify-end">
        <ThemedButton
          onClick={handleSavePreferences}
          disabled={saving || primarySaving}
          loading={saving}
          icon={Save}
        >
          {t('savePreferences')}
        </ThemedButton>
      </div>

      <ConfirmDialogShell
        isOpen={confirmPrimaryOpen}
        onClose={() => setConfirmPrimaryOpen(false)}
        onConfirm={confirmPrimaryEntity}
        title={t('confirmPrimaryEntityTitle')}
        confirmLabel={t('setPrimaryEntity')}
        isLoading={primarySaving}
        showIcon
        iconVariant="warning"
      >
        <p className={text.secondary}>
          {t('confirmPrimaryEntityMessage', {
            current: previousPrimary?.entityName || t('primaryEntity'),
            next: currentEntity.entityName
          })}
        </p>
      </ConfirmDialogShell>
    </div>
  );
};

export default PreferenceSettings;
